import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { fetchCustomerLoans } from '../../services/comersCustomerLoans';
import { formatCurrency, formatDate } from '../../utils/numberFormatter';
import { Button, TableCard } from '../ui';
import '../../styles/requests.css';
import '../../styles/requestStatusBadges.css';

const DEFAULT_PAGINATION = {
  total: 0,
  current_page: 1,
  per_page: 10,
  last_page: 1
};

const CustomerIssuedLoans = ({ clientData }) => {
  const customerIdentifier = useMemo(
    () => clientData?.uid || clientData?.id || '',
    [clientData?.id, clientData?.uid]
  );
  const [loans, setLoans] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const loadLoans = useCallback(async () => {
    if (!customerIdentifier) {
      setLoans([]);
      setErrorMessage('No pudimos identificar al cliente para consultar sus créditos.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const result = await fetchCustomerLoans(customerIdentifier, {
        page: pagination.current_page,
        per_page: pagination.per_page
      });

      setLoans(result.loans);
      setPagination((prev) => ({
        ...prev,
        ...result.pagination
      }));
    } catch (error) {
      console.error('Error al obtener créditos del cliente:', error);
      setLoans([]);
      setErrorMessage('No se pudieron cargar los créditos del cliente. Intentá nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [customerIdentifier, pagination.current_page, pagination.per_page]);

  useEffect(() => {
    loadLoans();
  }, [loadLoans]);

  const Pagination = () => (
    <nav aria-label="Navegación de créditos del cliente" className="requests-page__pagination-nav">
      <ul className="pagination justify-content-center mb-0">
        <li className={`page-item ${loading || pagination.current_page === 1 ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
            disabled={loading || pagination.current_page === 1}
          >
            Anterior
          </button>
        </li>
        {Array.from({ length: pagination.last_page }, (_, index) => (
          <li
            key={index}
            className={`page-item ${pagination.current_page === index + 1 ? 'active' : ''} ${loading ? 'disabled' : ''}`}
          >
            <button
              className="page-link"
              onClick={() => setPagination(prev => ({ ...prev, current_page: index + 1 }))}
              disabled={loading}
            >
              {index + 1}
            </button>
          </li>
        ))}
        <li className={`page-item ${loading || pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
            disabled={loading || pagination.current_page === pagination.last_page}
          >
            Siguiente
          </button>
        </li>
      </ul>
    </nav>
  );

  const TableSkeleton = () => (
    <>
      {Array.from({ length: Math.min(pagination.per_page, 5) }).map((_, index) => (
        <tr key={index}>
          <td data-label="Préstamo"><Skeleton width={90} height={16} /></td>
          <td data-label="Otorgamiento"><Skeleton width={88} height={14} /></td>
          <td data-label="Capital"><Skeleton width={96} height={16} /></td>
          <td data-label="Cuotas"><Skeleton width={34} height={16} /></td>
          <td data-label="Valor cuota"><Skeleton width={96} height={16} /></td>
          <td data-label="Estado" className="text-center"><Skeleton width={88} height={28} borderRadius={999} /></td>
        </tr>
      ))}
    </>
  );

  return (
    <div className="requests-page">
      <header className="requests-page__heading mb-3">
        <div>
          <h2 className="requests-page__title h4 mb-1">Créditos del cliente</h2>
          <p className="requests-page__lead mb-0">
            Consultá los créditos emitidos para este cliente en tu comercio.
          </p>
        </div>
        <Button variant="outlineSecondary" onClick={loadLoans} disabled={loading}>
          <i className={`bi bi-arrow-clockwise${loading ? ' rg-spin' : ''}`} aria-hidden />
          Actualizar
        </Button>
      </header>

      <TableCard
        className="requests-page__table-card"
        bodyClassName="requests-page__table-card-body"
        tableClassName="requests-page__table-wrap"
        paginationClassName="requests-page__pagination"
        header={(
          <div className="requests-page__context-bar">
            <div className="requests-page__context-copy">
              <span className="requests-page__context-eyebrow">Cliente</span>
              <p className="requests-page__context-text">
                {loading
                  ? 'Actualizando créditos...'
                  : `${pagination.total || 0} crédito${pagination.total === 1 ? '' : 's'} emitido${pagination.total === 1 ? '' : 's'}`}
              </p>
            </div>
          </div>
        )}
        table={
          <table className="table table-hover requests-page__table">
            <thead>
              <tr>
                <th>Préstamo</th>
                <th>Otorgamiento</th>
                <th>Capital</th>
                <th>Cuotas</th>
                <th>Valor cuota</th>
                <th className="text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton />
              ) : errorMessage ? (
                <tr>
                  <td colSpan="6" className="requests-page__empty-cell">
                    <i className="bi bi-exclamation-circle requests-page__empty-icon" aria-hidden />
                    <div className="requests-page__empty-title">No pudimos cargar los créditos</div>
                    <p className="requests-page__empty-copy">{errorMessage}</p>
                  </td>
                </tr>
              ) : loans.length === 0 ? (
                <tr>
                  <td colSpan="6" className="requests-page__empty-cell">
                    <i className="bi bi-inbox requests-page__empty-icon" aria-hidden />
                    <div className="requests-page__empty-title">Este cliente todavía no tiene créditos emitidos en este comercio.</div>
                    <p className="requests-page__empty-copy">
                      Cuando exista un préstamo emitido dentro de tu comercio o sucursal, aparecerá acá.
                    </p>
                  </td>
                </tr>
              ) : (
                loans.map((loan) => (
                  <tr key={loan.loan_uid || loan.loan_identifier}>
                    <td data-label="Préstamo">{loan.loan_identifier || loan.loan_uid || '—'}</td>
                    <td data-label="Otorgamiento">{formatDate(loan.granted_at) || '—'}</td>
                    <td data-label="Capital">{formatCurrency(loan.capital)}</td>
                    <td data-label="Cuotas" className="text-center">{loan.installments ?? '—'}</td>
                    <td data-label="Valor cuota">{loan.installment_amount != null ? formatCurrency(loan.installment_amount) : '—'}</td>
                    <td data-label="Estado" className="text-center">
                      <span className="request-status-badge request-status-approved">
                        {loan.status_label || 'Activo'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        }
        pagination={<Pagination />}
      />
    </div>
  );
};

export default CustomerIssuedLoans;
